import { zodResolver } from "@hookform/resolvers/zod";
import {
  DELIVERY_COMPARE_BASES,
  DELIVERY_COMPARE_BASIS_LABELS,
  FUEL_COST_SCOPE_LABELS,
  FUEL_COST_SCOPES,
  type FuelSettings,
  type SaveFuelSettings,
  TEMPERATURE_CORRECTION_STRATEGY_CODES,
  TEMPERATURE_STRATEGY_LABELS,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  FormField,
  Input,
  Select,
  SkeletonRows,
  StatusBadge,
  useToast,
} from "@station/ui";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Plus, Trash2 } from "lucide-react";
import { type ReactElement, type ReactNode, useEffect, useId, useState } from "react";
import { type FieldErrors, type Path, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useCompanyStations } from "../../../features/accounting/shared.js";
import { useFuelPermissions, useFuelSettings, useRefreshFuel } from "../../../features/fuel/queries.js";
import {
  type FuelSettingsFormValues,
  fuelSettingsFormFrom,
  fuelSettingsFormSchema,
} from "../../../features/fuel/settings-values.js";
import { FuelPage, fuelSearchShape } from "../../../features/fuel/shared.js";
import { api } from "../../../lib/api.js";
import { errorMessage, isApiError } from "../../../lib/errors.js";

const searchSchema = z.object({
  ...fuelSearchShape,
  /** "company" ya da bir istasyon kimliği: ayar satırı ŞİRKET ya da İSTASYON geçersiz kılmasıdır. */
  scope: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_app/akaryakit/ayarlar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: FuelSettingsPage,
});

function FuelSettingsPage(): ReactElement {
  const { t } = useTranslation();
  const permissions = useFuelPermissions();
  return (
    <FuelPage
      title={t("menu.fuelSettings")}
      description={t("fuel.settings.description")}
      breadcrumb={[{ label: t("menu.fuelSettings") }]}
    >
      {({ companyId }) =>
        permissions.canRead ? (
          <SettingsBody companyId={companyId} />
        ) : (
          <Alert tone="info">{t("fuel.noPermission")}</Alert>
        )
      }
    </FuelPage>
  );
}

function SettingsBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const stations = useCompanyStations(companyId);
  const scope = search.scope ?? "company";
  const scopeStationId = scope === "company" ? null : scope;
  const settings = useFuelSettings(companyId, scopeStationId);

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <FormField
          label={t("fuel.settings.scope")}
          description={t("fuel.settings.scopeHint")}
          className="w-72"
        >
          {(control) => (
            <Select
              {...control}
              value={scope}
              onChange={(event) =>
                void navigate({
                  search: (previous) => ({ ...previous, scope: event.target.value }),
                  replace: true,
                })
              }
            >
              <option value="company">{t("fuel.settings.companyScope")}</option>
              {(stations.data ?? []).map((station) => (
                <option key={station.id} value={station.id}>
                  {station.code} {station.name}
                </option>
              ))}
            </Select>
          )}
        </FormField>
      </Card>

      {settings.error ? (
        <Alert tone="danger">{errorMessage(settings.error)}</Alert>
      ) : !settings.data ? (
        <SkeletonRows rows={8} />
      ) : (
        <SettingsForm
          key={`${companyId}:${scope}`}
          settings={settings.data as FuelSettings}
          scopeStationId={scopeStationId}
          onReload={() => void settings.refetch()}
        />
      )}
    </>
  );
}

/** Yuva yollu (`a.b.c`) alan hatasını okur: hata her zaman ilgili alanın altında görünmelidir. */
function errorAt(errors: FieldErrors<FuelSettingsFormValues>, name: string): string | undefined {
  const node = name
    .split(".")
    .reduce<unknown>(
      (current, part) => (current as Record<string, unknown> | undefined)?.[part],
      errors as unknown,
    );
  const message = (node as { message?: unknown } | undefined)?.message;
  return typeof message === "string" ? message : undefined;
}

function SettingsForm({
  settings,
  scopeStationId,
  onReload,
}: {
  settings: FuelSettings;
  scopeStationId: string | null;
  onReload: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshFuel();
  const canManage = useFuelPermissions().canManageSettings;
  const [error, setError] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false);

  // İstasyon kapsamında henüz geçersiz kılma yoksa şirket satırının değerleri gösterilir; hedef sürüm 0'dır.
  const overrideExists = scopeStationId !== null && settings.stationId === scopeStationId;
  const isDefaultRow = scopeStationId === null ? settings.isDefault : !overrideExists;
  const targetVersion = scopeStationId === null ? settings.version : overrideExists ? settings.version : 0;

  const form = useForm<FuelSettingsFormValues, unknown, SaveFuelSettings>({
    resolver: zodResolver(fuelSettingsFormSchema),
    defaultValues: fuelSettingsFormFrom(settings, {
      stationId: scopeStationId,
      version: targetVersion,
    }),
  });
  const {
    formState: { errors },
    register,
    setValue,
    watch,
  } = form;

  useEffect(() => {
    form.reset(fuelSettingsFormFrom(settings, { stationId: scopeStationId, version: targetVersion }));
    // Sunucudan yeni ayar geldiğinde (yenile / kaydet sonrası) form tazelenir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings, scopeStationId, targetVersion]);

  const save = useMutation({
    mutationFn: async (body: SaveFuelSettings) =>
      (await api.PUT("/fuel/settings", { body: body as never })).data!,
    onSuccess: async (saved) => {
      await refresh();
      form.reset(
        fuelSettingsFormFrom(saved as FuelSettings, {
          stationId: scopeStationId,
          version: (saved as FuelSettings).version,
        }),
      );
      toast({ tone: "success", title: t("fuel.settings.saved") });
    },
    onError: (caught) => {
      if (isApiError(caught, "VERSION_CONFLICT")) {
        setConflict(true);
        return;
      }
      setError(errorMessage(caught));
    },
  });

  const submit = form.handleSubmit((values) => {
    setError(null);
    setConflict(false);
    save.mutate(values);
  });

  const needsVerification = watch("needsVerification");
  const compartments = watch("compartmentSizesL") ?? [];

  const field = (
    name: Path<FuelSettingsFormValues>,
    label: string,
    description: string,
    options: { integer?: boolean } = {},
  ): ReactElement => (
    <FormField label={label} description={description} error={errorAt(errors, name)}>
      {(control) => (
        <Input
          {...control}
          {...register(name)}
          {...(options.integer ? { type: "number", inputMode: "numeric" } : { inputMode: "decimal" })}
          disabled={!canManage}
        />
      )}
    </FormField>
  );

  return (
    <form className="grid gap-4" onSubmit={(event) => void submit(event)}>
      {conflict ? (
        <Alert tone="warning" title={t("fuel.settings.conflictTitle")}>
          {t("fuel.settings.conflictBody")}{" "}
          <Button variant="outline" size="sm" onClick={onReload}>
            {t("fuel.settings.reload")}
          </Button>
        </Alert>
      ) : null}
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {canManage ? null : <Alert tone="info">{t("fuel.settings.readOnly")}</Alert>}
      {isDefaultRow ? (
        <Alert tone="info" title={t("fuel.settings.isDefaultTitle")}>
          {scopeStationId === null ? t("fuel.settings.isDefaultBody") : t("fuel.settings.noOverrideBody")}
        </Alert>
      ) : null}
      {needsVerification ? (
        <Alert tone="warning" title={t("fuel.settings.needsVerificationTitle")}>
          <div className="grid gap-2">
            <p>{t("fuel.settings.needsVerificationBody")}</p>
            {canManage ? (
              <div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setValue("needsVerification", false, { shouldDirty: true });
                    void submit();
                  }}
                >
                  {t("fuel.settings.markVerified")}
                </Button>
              </div>
            ) : null}
          </div>
        </Alert>
      ) : (
        <p>
          <StatusBadge tone="success">{t("fuel.settings.verified")}</StatusBadge>
        </p>
      )}

      <FormSection title={t("fuel.settings.sections.volume")}>
        <FormField
          label={t("fuel.settings.temperatureStrategy")}
          description={t("fuel.settings.temperatureStrategyHint")}
          error={errorAt(errors, "temperatureStrategy")}
        >
          {(control) => (
            <Select {...control} {...register("temperatureStrategy")} disabled={!canManage}>
              {TEMPERATURE_CORRECTION_STRATEGY_CODES.map((code) => (
                <option key={code} value={code}>
                  {TEMPERATURE_STRATEGY_LABELS[code]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
      </FormSection>

      <FormSection title={t("fuel.settings.sections.delivery")}>
        <FormField
          label={t("fuel.settings.deliveryCompareBasis")}
          description={t("fuel.settings.deliveryCompareBasisHint")}
          error={errorAt(errors, "deliveryCompareBasis")}
        >
          {(control) => (
            <Select {...control} {...register("deliveryCompareBasis")} disabled={!canManage}>
              {DELIVERY_COMPARE_BASES.map((code) => (
                <option key={code} value={code}>
                  {DELIVERY_COMPARE_BASIS_LABELS[code]}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        {field(
          "deliveryTolerancePerMille",
          t("fuel.settings.deliveryTolerancePerMille"),
          t("fuel.settings.deliveryTolerancePerMilleHint"),
        )}
        {field(
          "deliveryToleranceMinL",
          t("fuel.settings.deliveryToleranceMinL"),
          t("fuel.settings.deliveryToleranceMinLHint"),
        )}
        {field("settleMinutes", t("fuel.settings.settleMinutes"), t("fuel.settings.settleMinutesHint"), {
          integer: true,
        })}
      </FormSection>

      {scopeStationId === null ? (
        <FormSection title={t("fuel.settings.sections.cost")}>
          <FormField
            label={t("fuel.settings.costScope")}
            description={t("fuel.settings.costScopeHint")}
            error={errorAt(errors, "costScope")}
          >
            {(control) => (
              <Select {...control} {...register("costScope")} disabled={!canManage}>
                {FUEL_COST_SCOPES.map((code) => (
                  <option key={code} value={code}>
                    {FUEL_COST_SCOPE_LABELS[code]}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
        </FormSection>
      ) : null}

      <FormSection title={t("fuel.settings.sections.reconciliation")}>
        {field(
          "normalLossPercent",
          t("fuel.settings.normalLossPercent"),
          t("fuel.settings.normalLossPercentHint"),
        )}
        {field(
          "reconciliationThresholds.dailyVariance.minimumL",
          t("fuel.settings.thresholds.dailyMinimumL"),
          t("fuel.settings.thresholds.dailyMinimumLHint"),
        )}
        {field(
          "reconciliationThresholds.dailyVariance.watchPercent",
          t("fuel.settings.thresholds.dailyWatchPercent"),
          t("fuel.settings.thresholds.dailyWatchPercentHint"),
        )}
        {field(
          "reconciliationThresholds.dailyVariance.suspiciousPercent",
          t("fuel.settings.thresholds.dailySuspiciousPercent"),
          t("fuel.settings.thresholds.dailySuspiciousPercentHint"),
        )}
        {field(
          "reconciliationThresholds.dailyVariance.criticalPercent",
          t("fuel.settings.thresholds.dailyCriticalPercent"),
          t("fuel.settings.thresholds.dailyCriticalPercentHint"),
        )}
        {field(
          "reconciliationThresholds.cumulativeTrend.periods",
          t("fuel.settings.thresholds.cumulativePeriods"),
          t("fuel.settings.thresholds.cumulativePeriodsHint"),
          { integer: true },
        )}
        {field(
          "reconciliationThresholds.cumulativeTrend.minimumNegativePeriods",
          t("fuel.settings.thresholds.cumulativeNegativePeriods"),
          t("fuel.settings.thresholds.cumulativeNegativePeriodsHint"),
          { integer: true },
        )}
        {field(
          "reconciliationThresholds.cumulativeTrend.suspiciousPercent",
          t("fuel.settings.thresholds.cumulativeSuspiciousPercent"),
          t("fuel.settings.thresholds.cumulativeSuspiciousPercentHint"),
        )}
        {field(
          "reconciliationThresholds.cumulativeTrend.criticalPercent",
          t("fuel.settings.thresholds.cumulativeCriticalPercent"),
          t("fuel.settings.thresholds.cumulativeCriticalPercentHint"),
        )}
        {field(
          "reconciliationThresholds.idleLevelDrop.suspiciousLPerHour",
          t("fuel.settings.thresholds.idleSuspicious"),
          t("fuel.settings.thresholds.idleSuspiciousHint"),
        )}
        {field(
          "reconciliationThresholds.idleLevelDrop.criticalLPerHour",
          t("fuel.settings.thresholds.idleCritical"),
          t("fuel.settings.thresholds.idleCriticalHint"),
        )}
        {field(
          "reconciliationThresholds.idleLevelDrop.minimumDropL",
          t("fuel.settings.thresholds.idleMinimumDropL"),
          t("fuel.settings.thresholds.idleMinimumDropLHint"),
        )}
        {field(
          "reconciliationThresholds.waterRise.watchMm",
          t("fuel.settings.thresholds.waterWatchMm"),
          t("fuel.settings.thresholds.waterWatchMmHint"),
        )}
        {field(
          "reconciliationThresholds.waterRise.criticalMm",
          t("fuel.settings.thresholds.waterCriticalMm"),
          t("fuel.settings.thresholds.waterCriticalMmHint"),
        )}
        {field(
          "reconciliationThresholds.salesCheck.watchL",
          t("fuel.settings.thresholds.salesWatchL"),
          t("fuel.settings.thresholds.salesWatchLHint"),
        )}
        {field(
          "reconciliationThresholds.salesCheck.suspiciousL",
          t("fuel.settings.thresholds.salesSuspiciousL"),
          t("fuel.settings.thresholds.salesSuspiciousLHint"),
        )}
      </FormSection>

      <FormSection title={t("fuel.settings.sections.totalizer")}>
        {field(
          "totalizerRolloverWindowL",
          t("fuel.settings.totalizerRolloverWindowL"),
          t("fuel.settings.totalizerRolloverWindowLHint"),
        )}
        {field(
          "mechanicalMismatchL",
          t("fuel.settings.mechanicalMismatchL"),
          t("fuel.settings.mechanicalMismatchLHint"),
        )}
      </FormSection>

      <FormSection title={t("fuel.settings.sections.price")}>
        {field(
          "priceGraceSeconds",
          t("fuel.settings.priceGraceSeconds"),
          t("fuel.settings.priceGraceSecondsHint"),
          {
            integer: true,
          },
        )}
      </FormSection>

      <FormSection title={t("fuel.settings.sections.forecast")}>
        {field("forecastWeeks", t("fuel.settings.forecastWeeks"), t("fuel.settings.forecastWeeksHint"), {
          integer: true,
        })}
        {field("leadTimeHours", t("fuel.settings.leadTimeHours"), t("fuel.settings.leadTimeHoursHint"), {
          integer: true,
        })}
        {field(
          "minimumStockDays",
          t("fuel.settings.minimumStockDays"),
          t("fuel.settings.minimumStockDaysHint"),
        )}
        <div className="grid gap-2 sm:col-span-2">
          <p className="text-sm font-medium">{t("fuel.settings.compartments")}</p>
          <p className="text-xs text-muted-foreground">{t("fuel.settings.compartmentsHint")}</p>
          {errorAt(errors, "compartmentSizesL") ? (
            <p role="alert" className="text-xs font-medium text-destructive">
              {errorAt(errors, "compartmentSizesL")}
            </p>
          ) : null}
          <ul className="grid gap-2">
            {compartments.map((_size, index) => (
              <li key={index} className="flex items-end gap-2">
                <FormField
                  label={t("fuel.settings.compartmentSize", { index: index + 1 })}
                  error={errorAt(errors, `compartmentSizesL.${index}`)}
                  className="w-48"
                >
                  {(control) => (
                    <Input
                      {...control}
                      {...register(`compartmentSizesL.${index}` as Path<FuelSettingsFormValues>)}
                      inputMode="decimal"
                      disabled={!canManage}
                    />
                  )}
                </FormField>
                {canManage ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="min-h-6"
                    aria-label={t("fuel.settings.removeCompartment", { index: index + 1 })}
                    onClick={() =>
                      setValue(
                        "compartmentSizesL",
                        compartments.filter((__, position) => position !== index),
                        { shouldDirty: true },
                      )
                    }
                  >
                    <Trash2 aria-hidden="true" />
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
          {canManage ? (
            <div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setValue("compartmentSizesL", [...compartments, ""], { shouldDirty: true })}
              >
                <Plus aria-hidden="true" />
                {t("fuel.settings.addCompartment")}
              </Button>
            </div>
          ) : null}
        </div>
      </FormSection>

      {scopeStationId === null ? (
        <FormSection title={t("fuel.settings.sections.accounting")}>
          <label className="flex min-h-6 items-start gap-2 text-sm sm:col-span-2">
            <Checkbox className="mt-1" {...register("postDailySalesSummary")} disabled={!canManage} />
            <span className="grid gap-0.5">
              <span className="font-medium">{t("fuel.settings.postDailySalesSummary")}</span>
              <span className="text-xs text-muted-foreground">
                {t("fuel.settings.postDailySalesSummaryHint")}
              </span>
            </span>
          </label>
        </FormSection>
      ) : null}

      {canManage ? (
        <div className="flex justify-end">
          <Button type="submit" loading={save.isPending}>
            {t("common.save")}
          </Button>
        </div>
      ) : null}
    </form>
  );
}

function FormSection({ title, children }: { title: string; children: ReactNode }): ReactElement {
  const headingId = useId();
  return (
    <Card className="grid gap-4 p-4 sm:p-5" aria-labelledby={headingId}>
      <h2 id={headingId} className="text-base font-semibold">
        {title}
      </h2>
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">{children}</div>
    </Card>
  );
}
